/**
 * Minimal VAST 2.0/3.0/4.0 parser
 * Extracts: video URL, tracking event URLs, skip offset, duration
 * Handles wrapper chains (follows redirects up to 3 hops)
 */

export interface VASTMediaFile {
  url: string
  type: string
  width: number
  height: number
  bitrate: number
}

/**
 * A NonLinear creative — VAST's format for a static overlay (image banner)
 * shown alongside/instead of actual video playback, as opposed to Linear
 * (the video itself). ExoClick's "In-Video Banner (Pause)" zone type
 * returns exactly this: a <NonLinearAds><NonLinear> with a <StaticResource>
 * image and its own <NonLinearClickThrough>, no <MediaFile> at all — a
 * real display banner delivered inside a VAST envelope, not a video.
 */
export interface VASTNonLinearAd {
  imageUrl: string
  clickThrough: string | null
  width: number
  height: number
}

export interface VASTTrackingEvents {
  start?: string[]
  firstQuartile?: string[]
  midpoint?: string[]
  thirdQuartile?: string[]
  complete?: string[]
  skip?: string[]
  impression?: string[]
  clickThrough?: string
  clickTracking?: string[]
}

export interface VASTResult {
  mediaFiles: VASTMediaFile[]
  nonLinearAds: VASTNonLinearAd[]
  tracking: VASTTrackingEvents
  skipOffset: number | null  // seconds, null = non-skippable
  duration: number           // seconds
  adTitle?: string
}

function parseOffset(offset: string | null): number | null {
  if (!offset) return null
  // "00:00:05" format
  if (offset.includes(":")) {
    const parts = offset.split(":").map(Number)
    return parts[0] * 3600 + parts[1] * 60 + parts[2]
  }
  // percentage "25%" — return null, handle as non-skippable
  if (offset.endsWith("%")) return null
  return parseInt(offset) || null
}

function parseDuration(dur: string | null): number {
  if (!dur) return 30
  if (dur.includes(":")) {
    const parts = dur.split(":").map(Number)
    return parts[0] * 3600 + parts[1] * 60 + parts[2]
  }
  return parseInt(dur) || 30
}

function extractFromDoc(doc: Document): VASTResult | null {
  // Try InLine first, then Wrapper
  const inline = doc.querySelector("InLine")
  const wrapper = doc.querySelector("Wrapper")
  const node = inline || wrapper

  if (!node) return null

  // Media files (only in InLine)
  const mediaFiles: VASTMediaFile[] = []
  node.querySelectorAll("MediaFile").forEach((mf) => {
    const url = mf.textContent?.trim()
    if (!url) return
    const type = mf.getAttribute("type") || "video/mp4"
    // Only take video types, skip VPAID JS
    if (type.startsWith("video/") || type === "video/mp4") {
      mediaFiles.push({
        url,
        type,
        width: parseInt(mf.getAttribute("width") || "0"),
        height: parseInt(mf.getAttribute("height") || "0"),
        bitrate: parseInt(mf.getAttribute("bitrate") || "0"),
      })
    }
  })

  // Sort by bitrate descending — pick best quality
  mediaFiles.sort((a, b) => b.bitrate - a.bitrate)

  // NonLinear (static image banner) creatives — see VASTNonLinearAd above.
  const nonLinearAds: VASTNonLinearAd[] = []
  node.querySelectorAll("NonLinear").forEach((nl) => {
    const staticResource = nl.querySelector("StaticResource")
    const imageUrl = staticResource?.textContent?.trim()
    if (!imageUrl) return
    const clickThrough = nl.querySelector("NonLinearClickThrough")?.textContent?.trim() || null
    nonLinearAds.push({
      imageUrl,
      clickThrough,
      width: parseInt(nl.getAttribute("width") || "0"),
      height: parseInt(nl.getAttribute("height") || "0"),
    })
  })

  // Tracking events
  const tracking: VASTTrackingEvents = {
    impression: [],
    start: [],
    firstQuartile: [],
    midpoint: [],
    thirdQuartile: [],
    complete: [],
    skip: [],
    clickTracking: [],
  }

  node.querySelectorAll("Impression").forEach((el) => {
    const url = el.textContent?.trim()
    if (url) tracking.impression!.push(url)
  })

  node.querySelectorAll("Tracking").forEach((el) => {
    const event = el.getAttribute("event")
    const url = el.textContent?.trim()
    if (!event || !url) return
    const key = event as keyof VASTTrackingEvents
    if (Array.isArray(tracking[key])) {
      (tracking[key] as string[]).push(url)
    }
  })

  const clickThrough = node.querySelector("ClickThrough")?.textContent?.trim()
  if (clickThrough) tracking.clickThrough = clickThrough

  node.querySelectorAll("ClickTracking").forEach((el) => {
    const url = el.textContent?.trim()
    if (url) tracking.clickTracking!.push(url)
  })

  // Skip offset
  const linear = node.querySelector("Linear")
  const skipOffsetAttr = linear?.getAttribute("skipoffset") || null
  const skipOffset = parseOffset(skipOffsetAttr)

  // Duration
  const durationEl = node.querySelector("Duration")
  const duration = parseDuration(durationEl?.textContent?.trim() || null)

  // Title
  const adTitle = node.querySelector("AdTitle")?.textContent?.trim()

  return { mediaFiles, nonLinearAds, tracking, skipOffset, duration, adTitle }
}

async function fetchVAST(url: string, hop = 0): Promise<VASTResult | null> {
  if (hop > 3) return null // max wrapper chain depth

  try {
    const res = await fetch(`/api/ads/vast-proxy?url=${encodeURIComponent(url)}`)
    if (!res.ok) return null
    let xml = await res.text()

    // Real ad fill routinely comes back as VAST 4.x declaring a default
    // namespace (xmlns="http://www.iab.com/VAST") — confirmed live from a
    // real wrapped ExoClick response with a genuine playable MediaFile.
    // querySelector's unprefixed type selectors ("InLine", "MediaFile", ...)
    // used throughout extractFromDoc() only match elements with NO
    // namespace, so every real ad using this common VAST 4 convention was
    // silently parsing to zero media files and getting dismissed as "no
    // fill" — while the genuinely-empty no-fill responses (which never
    // declare a default namespace) parsed "correctly" into nothing. That's
    // backwards from what should happen, and is why pre-roll never visibly
    // played even when the network had real inventory. Stripping the
    // default `xmlns="..."` attribute (not prefixed ones like xmlns:xsi,
    // which don't affect unprefixed selector matching) before parsing makes
    // every element namespace-less so the existing querySelector calls work
    // as originally intended, without touching any of the extraction logic.
    xml = xml.replace(/\sxmlns="[^"]*"/g, "")

    const parser = new DOMParser()
    const doc = parser.parseFromString(xml, "text/xml")

    // Check for wrapper redirect
    const wrapperUri = doc.querySelector("VASTAdTagURI")?.textContent?.trim()
    if (wrapperUri) {
      // Follow the wrapper
      const wrapped = await fetchVAST(wrapperUri, hop + 1)
      if (wrapped) {
        // Merge wrapper tracking with inner ad tracking
        const outerResult = extractFromDoc(doc)
        if (outerResult) {
          const mergeArrays = (a?: string[], b?: string[]) => [...(a || []), ...(b || [])]
          wrapped.tracking.impression = mergeArrays(outerResult.tracking.impression, wrapped.tracking.impression)
          wrapped.tracking.start = mergeArrays(outerResult.tracking.start, wrapped.tracking.start)
          wrapped.tracking.complete = mergeArrays(outerResult.tracking.complete, wrapped.tracking.complete)
        }
        return wrapped
      }
    }

    return extractFromDoc(doc)
  } catch {
    return null
  }
}

export async function parseVAST(vastUrl: string): Promise<VASTResult | null> {
  return fetchVAST(vastUrl, 0)
}

export async function fireTrackingUrl(url: string): Promise<void> {
  try {
    // Use sendBeacon for reliability, fallback to fetch
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url)
    } else {
      fetch(url, { mode: "no-cors", keepalive: true }).catch(() => {})
    }
  } catch {
    // Silently fail — tracking should never break the ad
  }
}

export function fireTrackingUrls(urls: string[] | undefined): void {
  if (!urls) return
  urls.forEach(fireTrackingUrl)
}
